import React, { useEffect, useMemo, useState } from 'react';
import './AdminPage.css';
import { useContent, type ContentSectionKey } from '../contexts/ContentContext';
import { isSupabaseConfigured, supabase } from '../lib/supabase';

type AuthMode = 'login' | 'ready';

type AdminProfile = {
  role: string;
  is_active: boolean;
};

const sectionKeys: ContentSectionKey[] = [
  'home',
  'featured',
  'events',
  'workshops',
  'gallery',
  'team',
  'about',
  'contact',
  'notifications'
];

const pretty = (value: unknown) => JSON.stringify(value, null, 2);

const AdminPage = () => {
  const { getSection, refreshContent } = useContent();
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [checking, setChecking] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loadingAuth, setLoadingAuth] = useState(false);
  const [selectedKey, setSelectedKey] = useState<ContentSectionKey>('home');
  const [editorValue, setEditorValue] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const title = useMemo(() => `Editing: ${selectedKey}`, [selectedKey]);

  const checkAdminAccess = async (uid: string, userEmail?: string | null) => {
    if (!supabase) {
      setAllowed(false);
      return;
    }

    const fetchAdminProfile = async (field: 'user_id' | 'email', value: string) => {
      const { data, error } = await supabase
        .from('admin_profiles')
        .select('role, is_active')
        .eq(field, value)
        .maybeSingle();

      if (error) {
        return null;
      }

      return data as AdminProfile | null;
    };

    const profileByUserId = await fetchAdminProfile('user_id', uid);
    const profileByEmail = userEmail ? await fetchAdminProfile('email', userEmail) : null;
    const profile = profileByUserId || profileByEmail;

    if (!profile || !profile.is_active) {
      setAllowed(false);
      return;
    }

    setAllowed(profile.role === 'admin' || profile.role === 'editor');
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        if (!isSupabaseConfigured || !supabase) {
          setAuthMode('login');
          return;
        }

        const { data } = await supabase.auth.getSession();
        const uid = data.session?.user?.id || null;
        const userEmail = data.session?.user?.email || null;
        setUserId(uid);

        if (uid) {
          await checkAdminAccess(uid, userEmail);
          setAuthMode('ready');
        } else {
          setAllowed(false);
          setAuthMode('login');
        }
      } catch (error) {
        console.error('Admin initialization failed', error);
        setAllowed(false);
        setAuthMode('login');
      } finally {
        setChecking(false);
      }
    };

    initialize();

    if (!supabase) {
      return;
    }

    const { data: listener } = supabase.auth.onAuthStateChange(async (_event, session) => {
      const uid = session?.user?.id || null;
      const userEmail = session?.user?.email || null;
      setUserId(uid);
      setSuccessMessage(null);
      setErrorMessage(null);

      if (uid) {
        await checkAdminAccess(uid, userEmail);
        setAuthMode('ready');
      } else {
        setAllowed(false);
        setAuthMode('login');
      }
    });

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const sectionData = getSection<unknown>(selectedKey, {});
    setEditorValue(pretty(sectionData));
    setIsDirty(false);
    setErrorMessage(null);
    setSuccessMessage(null);
  }, [selectedKey, getSection]);

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!supabase) {
      return;
    }

    setLoadingAuth(true);
    setErrorMessage(null);

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      setErrorMessage(error.message);
    }

    setLoadingAuth(false);
  };

  const handleLogout = async () => {
    if (!supabase) {
      setAllowed(false);
      setAuthMode('login');
      return;
    }

    setErrorMessage(null);
    setSuccessMessage(null);
    setPassword('');
    setAllowed(false);
    setAuthMode('login');
    setUserId(null);

    try {
      await supabase.auth.signOut({ scope: 'global' });
    } catch (error) {
      console.error('Sign out failed', error);
    }
  };

  const handleSave = async () => {
    setErrorMessage(null);
    setSuccessMessage(null);

    if (!supabase || !userId) {
      setErrorMessage('Supabase is not configured or user session is missing.');
      return;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(editorValue);
    } catch {
      setErrorMessage('Invalid JSON. Please fix syntax before saving.');
      return;
    }

    setSaving(true);

    const { error } = await supabase.from('content_sections').upsert(
      {
        section_key: selectedKey,
        content: parsed,
        is_published: true,
        updated_by: userId
      },
      { onConflict: 'section_key' }
    );

    if (error) {
      setErrorMessage(error.message);
      setSaving(false);
      return;
    }

    await refreshContent();
    setSuccessMessage(`Saved ${selectedKey} successfully.`);
    setIsDirty(false);
    setSaving(false);
  };

  if (!isSupabaseConfigured) {
    return (
      <div className="admin-page">
        <div className="admin-card">
          <h1>Admin Setup Required</h1>
          <p>
            Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY
            in your environment to use this admin page.
          </p>
        </div>
      </div>
    );
  }

  if (checking) {
    return (
      <div className="admin-page">
        <div className="admin-card">
          <h1>Loading admin...</h1>
        </div>
      </div>
    );
  }

  if (authMode === 'login') {
    return (
      <div className="admin-page">
        <form className="admin-card admin-login" onSubmit={handleLogin}>
          <h1>Admin Login</h1>
          <p>Sign in with your Supabase admin account.</p>

          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>

          <label>
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>

          {errorMessage && <div className="admin-error">{errorMessage}</div>}

          <button type="submit" disabled={loadingAuth}>
            {loadingAuth ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="admin-page">
        <div className="admin-card">
          <h1>Access Denied</h1>
          <p>
            Your account does not match an active admin_profiles entry by auth user id or email,
            or the role is not set to admin/editor.
          </p>
          <button type="button" onClick={handleLogout}>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div>
          <h1>ASIMO Content Admin</h1>
          <p>Manage all JSON-backed website sections from one place.</p>
        </div>
        <button className="admin-logout" onClick={handleLogout}>
          Sign out
        </button>
      </header>

      <div className="admin-layout">
        <aside className="admin-sidebar">
          <h2>Sections</h2>
          <ul>
            {sectionKeys.map((key) => (
              <li key={key}>
                <button
                  className={selectedKey === key ? 'active' : ''}
                  onClick={() => setSelectedKey(key)}
                >
                  {key}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className="admin-editor">
          <div className="admin-editor-top">
            <h2>{title}</h2>
            <div className="admin-actions">
              {isDirty && <span className="admin-dirty">Unsaved changes</span>}
              <button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>

          <textarea
            value={editorValue}
            onChange={(e) => {
              setEditorValue(e.target.value);
              setIsDirty(true);
              setSuccessMessage(null);
            }}
            spellCheck={false}
          />

          {errorMessage && <div className="admin-error">{errorMessage}</div>}
          {successMessage && <div className="admin-success">{successMessage}</div>}
        </section>
      </div>
    </div>
  );
};

export default AdminPage;
